import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Loader2, Sparkles, ChevronLeft, Check, Ban, ThumbsUp, ThumbsDown, FileSearch } from 'lucide-react';
import {
  ClockIcon,
  TrashIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  XMarkIcon,
  DocumentTextIcon,
  DocumentMinusIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

import { chatService, ChatMessage, EditSuggestion, ChatResponseMetadata, StreamCallbacks } from '../services/chatService';
import { chatOnboardingService } from '../services/chatOnboardingService';
import { buildProposalAIContext, RoadmapContextItem } from '../services/chatContextService';
import { chatMemoryService, ChatHistorySummary } from '../services/chatMemoryService';
import { useChatInlineEdit } from '../hooks/useChatInlineEdit';
import { useFloatingPanel } from '../hooks/useFloatingPanel';
import { MarkdownRenderer } from './MarkdownRenderer';

interface ChatIAWidgetProps {
  proposalId?: number | null;
  onOpenChange?: (isOpen: boolean) => void;
}

const INITIAL_MESSAGE: ChatMessage = {
  role: 'assistant',
  content: 'Olá! 👋 Sou o especialista da PrAInte. Posso ajudar com dúvidas sobre a proposta, sugerir melhorias no conteúdo ou responder perguntas sobre o edital. Como posso ajudar?',
};

// Espelha o backend (api/chat_api.py::_should_use_onboarding): esses estados contam como
// "terminados" pro passo a passo. Sem isso, uma vez que o backend liga `onboarding=true`
// aqui no cliente, todo request seguinte reenvia `onboarding: true` e o backend nem chega
// a olhar pro estado da sessão (curto-circuita em `if data.onboarding: return True`) — o
// chat fica preso no wizard pra sempre, mesmo depois da elaboração terminar, até o usuário
// clicar manualmente em "Sair do passo a passo".
const ONBOARDING_TERMINAL_STATES = ['concluido', 'abortado', 'elaboracao_concluida'];
const isOnboardingTerminal = (state?: string | null) => !state || ONBOARDING_TERMINAL_STATES.includes(state);

function getDynamicQuickActions(
  roadmapItems: RoadmapContextItem[],
  checklistLen: number,
  docChecklistLen: number
): Array<{ label: string; prompt: string }> {
  const actions: Array<{ label: string; prompt: string }> = [];

  const pending = roadmapItems.filter(r => !r.content?.trim());
  if (roadmapItems.length === 0) {
    actions.push({ label: '✨ Montar a proposta comigo', prompt: 'Quero montar a proposta completa. Me guie pelo passo a passo: mapa conceitual, roteiro e conteúdo de todas as seções.' });
    actions.push({ label: 'Criar roteiro', prompt: 'Ainda não tenho um roteiro criado. Quais seções você sugere para esta proposta?' });
  } else if (pending.length > 0) {
    const first = pending[0];
    actions.push({ label: `Elaborar "${first.topic}"`, prompt: `Elabore um conteúdo completo para a seção "${first.topic}" do roteiro.` });
  }

  if (checklistLen > 0) {
    actions.push({ label: 'Avaliar checklist', prompt: 'Com base no conteúdo da proposta, avalie os itens do checklist de enquadramento.' });
  }
  if (docChecklistLen > 0) {
    actions.push({ label: 'Revisar documentos', prompt: 'Analise os documentos do checklist documental e indique quais estão conformes.' });
  }

  // Sempre inclui ações gerais como complemento
  actions.push({ label: 'O que falta?', prompt: 'Analise o estado atual da minha proposta e diga o que ainda está faltando ou pode ser melhorado.' });
  if (actions.length < 4) {
    actions.push({ label: 'Verificar o edital', prompt: 'Verifique se minha proposta está adequada aos requisitos e critérios do edital.' });
  }

  return actions.slice(0, 4);
}

export const ChatIAWidget: React.FC<ChatIAWidgetProps> = ({ proposalId, onOpenChange }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([INITIAL_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastMetadata, setLastMetadata] = useState<ChatResponseMetadata | null>(null);
  const [proposalContext, setProposalContext] = useState<string>('');
  const [proposalTitle, setProposalTitle] = useState<string>('');
  const [useProposalContext, setUseProposalContext] = useState<boolean>(true);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [showHistoryNotification, setShowHistoryNotification] = useState(false);
  const [showHistoryBrowser, setShowHistoryBrowser] = useState(false);
  const [historyList, setHistoryList] = useState<ChatHistorySummary[]>([]);
  const {
    panelRef,
    width: chatWidth,
    height: chatHeight,
    dragOffset,
    handleTopResize,
    handleLeftResize,
    handleCornerResize,
    handleDragStart,
  } = useFloatingPanel({ isOpen, initialWidth: 380, initialHeight: 520, minWidth: 300, minHeight: 300 });
  const [isExpanded, setIsExpanded] = useState(false);
  const [chatBtnHovered, setChatBtnHovered] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  // Onboarding conversacional (construir proposta pelo chat)
  const [onboarding, setOnboarding] = useState(false);
  const [onbState, setOnbState] = useState<{ state: string; step: number; total_steps: number } | null>(null);
  const [pendingCall, setPendingCall] = useState<{ call_id: string; name: string } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState<Map<number, 1 | -1>>(new Map());

  // Ao entrar no modo "construir proposta" (pedido do usuário ou retomada de sessão),
  // abre o chat em tela cheia uma vez — se a pessoa reduzir depois, não força de volta.
  const autoExpandedForOnboardingRef = useRef(false);
  useEffect(() => {
    if (onboarding && !autoExpandedForOnboardingRef.current) {
      autoExpandedForOnboardingRef.current = true;
      setIsOpen(true);
      setIsExpanded(true);
    }
    if (!onboarding) autoExpandedForOnboardingRef.current = false;
  }, [onboarding]);

  const roadmapItemsRef = useRef<RoadmapContextItem[]>([]);
  const conceptMapRef = useRef<Record<string, unknown>>({});
  const checklistItemsRef = useRef<Record<string, unknown>[]>([]);
  const docChecklistItemsRef = useRef<Record<string, unknown>[]>([]);
  const prevProposalIdRef = useRef<number | null | undefined>(undefined);
  // Incrementado sempre que a conversa é resetada (limpar histórico, trocar de
  // proposta). Uma resposta de IA em voo captura o valor atual no início do
  // envio; se o valor mudar antes dela terminar, a resposta é descartada em vez
  // de ser anexada a uma conversa que já não é mais a que a originou.
  const conversationGenerationRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const addMessage = useCallback((msg: ChatMessage) => {
    setMessages(prev => [...prev, msg]);
  }, []);

  const { pendingEdit, setPendingEdit, applyingEdit, apply: applyEdit, reject: rejectEdit, getCurrentContent } = useChatInlineEdit({
    proposalId,
    conceptMapRef,
    checklistItemsRef,
    docChecklistItemsRef,
    roadmapItemsRef,
    addMessage,
    onContextInvalidated: () => setProposalContext(''),
  });


  // Mantém a conversa posicionada na mensagem mais recente, inclusive ao reabrir
  // o widget com um histórico que já está carregado.
  useEffect(() => {
    if (!isOpen) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [isOpen, messages, streamingText]);
  useEffect(() => { if (isOpen && inputRef.current) inputRef.current.focus(); }, [isOpen]);

  // Avisa o componente pai (App) quando o chat abre/fecha, para que outros
  // elementos flutuantes (ex: modal de Feedback) possam reagir e não sobrepor o painel.
  useEffect(() => { onOpenChange?.(isOpen); }, [isOpen, onOpenChange]);
  useEffect(() => () => onOpenChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset ao trocar de proposta
  useEffect(() => {
    const prev = prevProposalIdRef.current;
    if (prev !== undefined && prev !== proposalId) {
      conversationGenerationRef.current += 1;
      setMessages([INITIAL_MESSAGE]);
      setProposalContext(''); setProposalTitle(''); setHistoryLoaded(false);
      setShowHistoryBrowser(false); setLastMetadata(null);
      roadmapItemsRef.current = []; conceptMapRef.current = {};
      checklistItemsRef.current = []; docChecklistItemsRef.current = [];
    }
    prevProposalIdRef.current = proposalId ?? null;
  }, [proposalId]);

  // Carrega contexto da proposta. Retorna o contexto buscado (não só via setState,
  // que é assíncrono) pra quem chama poder usar o valor fresco imediatamente.
  const loadContext = useCallback(async () => {
    if (!proposalId) return null;
    try {
      const ctx = await buildProposalAIContext(proposalId);
      setProposalTitle(ctx.proposalTitle);
      setProposalContext(ctx.contextText);
      roadmapItemsRef.current = ctx.roadmapItems;
      conceptMapRef.current = ctx.conceptMap;
      checklistItemsRef.current = ctx.checklistItems;
      docChecklistItemsRef.current = ctx.docChecklistItems;
      return ctx;
    } catch {
      return null;
    }
  }, [proposalId]);

  // Carrega histórico ao abrir
  useEffect(() => {
    if (!isOpen || historyLoaded) return;
    if (!proposalId) { setHistoryLoaded(true); return; }
    // Tenta backend primeiro, fallback localStorage
    chatService.getHistory(proposalId).then(serverMsgs => {
      if (serverMsgs.length > 0) {
        setMessages(serverMsgs);
        setShowHistoryNotification(true);
        setTimeout(() => setShowHistoryNotification(false), 3000);
      } else {
        const local = chatMemoryService.loadHistory(proposalId);
        if (local && local.length > 0) {
          setMessages(local);
          setShowHistoryNotification(true);
          setTimeout(() => setShowHistoryNotification(false), 3000);
        }
      }
      setHistoryLoaded(true);
    }).catch(() => {
      const local = chatMemoryService.loadHistory(proposalId);
      if (local && local.length > 0) { setMessages(local); }
      setHistoryLoaded(true);
    });
  }, [isOpen, historyLoaded, proposalId]);

  // Persiste histórico quando mensagens mudam (ambos: localStorage + backend)
  useEffect(() => {
    if (!proposalId || !historyLoaded || messages.length <= 1) return;
    chatMemoryService.saveHistory(proposalId, messages, proposalTitle || undefined);
    chatService.saveHistory(proposalId, messages);
  }, [messages, proposalId, historyLoaded, proposalTitle]);

  // Carrega contexto quando chat abre
  useEffect(() => {
    if (!isOpen) return;
    if (!proposalId) { setProposalContext(''); setProposalTitle(''); return; }
    if (!proposalContext) loadContext();
  }, [isOpen, proposalId, proposalContext, loadContext]);

  // Retoma o onboarding: se há sessão em andamento pra esta proposta (ou a auto-open
  // vinda de um fork "criar proposta pelo chat"), religa o modo e o card de progresso.
  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    (async () => {
      const auto = sessionStorage.getItem('chatia:onboarding-active');
      const isAutoForThis = auto && proposalId && Number(auto) === proposalId;
      const sess = await chatOnboardingService.getSession(proposalId ?? null);
      if (!active) return;
      // Sessões salvas não reativam o passo a passo ao abrir uma proposta comum.
      // A exceção é o handoff explícito após criar uma proposta pelo próprio chat.
      if (isAutoForThis && sess && !isOnboardingTerminal(sess.state)) {
        setOnboarding(true);
        setOnbState({ state: sess.state, step: sess.step, total_steps: sess.total_steps });
        sessionStorage.removeItem('chatia:onboarding-active');
      }
    })();
    return () => { active = false; };
  }, [isOpen, proposalId]);

  // Escuta evento de abertura externa
  useEffect(() => {
    const handler = (e: CustomEvent<{ prompt: string }>) => {
      setInput(e.detail.prompt);
      setIsOpen(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    };
    window.addEventListener('chatia:open-with-context', handler as EventListener);
    return () => window.removeEventListener('chatia:open-with-context', handler as EventListener);
  }, []);

  const sendMessageText = async (text: string) => {
    if (!text || loading) return;
    // Captura a geração da conversa atual — se o usuário limpar o histórico ou
    // trocar de proposta antes desta resposta chegar, conversationGenerationRef
    // muda e os callbacks abaixo descartam o resultado em vez de aplicá-lo a
    // uma conversa que já não é mais esta.
    const generation = conversationGenerationRef.current;
    const isStale = () => conversationGenerationRef.current !== generation;

    const userMessage: ChatMessage = { role: 'user', content: text };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    setLastMetadata(null);
    setStreamingText('');

    // Recarrega o contexto da proposta antes de cada envio — sem isso, o contexto
    // ficava congelado no que existia quando o chat foi aberto pela primeira vez
    // nesta sessão, então edições salvas depois (Roteiro, Mapa Conceitual etc.)
    // nunca chegavam à IA até o usuário trocar de proposta e voltar. Usa o valor
    // retornado diretamente (não o state, que só atualiza no próximo render).
    const freshContext = proposalId && useProposalContext ? await loadContext() : null;
    const currentProposalContext = freshContext ? freshContext.contextText : proposalContext;

    const fullHistory = [...messages, userMessage];

    try {
      let accumulatedText = '';
      let receivedMetadata: ChatResponseMetadata | null = null;
      let newProposalId: number | null = null;

      // O chat pode entrar no modo "construir proposta" por conta própria quando o
      // usuário pede (o backend decide). Estes callbacks são inofensivos fora desse
      // modo — os eventos só chegam quando o agente está ativo.
      const onbCallbacks: Partial<StreamCallbacks> = {
        onState: (ev) => {
          if (isStale()) return;
          setOnboarding(!isOnboardingTerminal(ev.state));
          setOnbState({ state: ev.state, step: ev.step, total_steps: ev.total_steps });
        },
        onToolResult: (ev) => {
          if (isStale()) return;
          if (ev.navigate_tab) window.dispatchEvent(new CustomEvent('chatia:navigate-tab', { detail: { tab: ev.navigate_tab } }));
        },
        // NÃO navega aqui: trocar de rota no meio do stream muda o proposalId, dispara o
        // reset do widget e o resto do stream vira "stale" (spinner travava). Guarda o id
        // e navega só depois que o stream inteiro (incl. `done`) foi consumido.
        onProposalCreated: (id) => { if (!isStale()) newProposalId = id; },
      };

      await chatService.sendMessageStream(
        {
          proposal_id: proposalId,
          message: text,
          history: fullHistory.filter(m => m.role !== 'system'),
          llm_provider: null,
          llm_model: null,
          proposal_context: useProposalContext ? currentProposalContext : '',
          roadmap_items: roadmapItemsRef.current.map(r => ({
            id: r.id,
            topic: r.topic,
            character_limit: r.character_limit,
            word_limit: r.word_limit,
            limit_type: r.limit_type,
          })),
          concept_map: Object.keys(conceptMapRef.current).length > 0 ? conceptMapRef.current : null,
          checklist_items: checklistItemsRef.current.length > 0 ? checklistItemsRef.current : null,
          doc_checklist_items: docChecklistItemsRef.current.length > 0 ? docChecklistItemsRef.current : null,
          onboarding,
        },
        {
          onMetadata: (meta) => { if (isStale()) return; receivedMetadata = meta; setLastMetadata(meta); },
          onChunk: (chunk) => { if (isStale()) return; accumulatedText += chunk; setStreamingText(accumulatedText); },
          onEditSuggestion: (suggestion) => { if (isStale()) return; setPendingEdit(suggestion); },
          onDone: (finalText, extra) => {
            if (isStale()) return;
            setMessages(prev => [...prev, { role: 'assistant', content: finalText ?? accumulatedText }]);
            setStreamingText('');
            // extra.state só vem do agente de onboarding; extra.pending_call vem também
            // do agente freeform (ação pedida no chat normal), sem entrar no modo guiado.
            if (extra?.state) setOnboarding(!isOnboardingTerminal(extra.state));
            if (extra?.state || extra?.pending_call) setPendingCall(extra?.pending_call ?? null);
          },
          onError: (err) => {
            if (isStale()) return;
            setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${err}` }]);
            setStreamingText('');
          },
          ...onbCallbacks,
        }
      );
      // Stream inteiro consumido — agora sim, se o agente criou a proposta, salva a
      // conversa nela e navega (isso remonta o widget com o novo proposalId).
      if (newProposalId && !isStale()) {
        try { await chatService.saveHistory(newProposalId, [...fullHistory, { role: 'assistant', content: accumulatedText }]); } catch { /* best-effort */ }
        sessionStorage.setItem('chatia:onboarding-active', String(newProposalId));
        navigate(`/proposals/${newProposalId}`);
      }
    } catch (error: any) {
      if (!isStale()) {
        setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${error.message || 'Erro ao processar mensagem. Tente novamente.'}` }]);
        setStreamingText('');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    await sendMessageText(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };


  const handleConfirmCall = async (decision: 'approve' | 'reject') => {
    if (!pendingCall || confirming) return;
    setConfirming(true);
    const call = pendingCall;
    setPendingCall(null);
    let acc = '';
    try {
      await chatOnboardingService.confirm(
        { proposalId: proposalId ?? null, callId: call.call_id, decision },
        {
          onChunk: (c) => { acc += c; setStreamingText(acc); },
          onState: (ev) => setOnbState({ state: ev.state, step: ev.step, total_steps: ev.total_steps }),
          onToolResult: (ev) => { if (ev.navigate_tab) window.dispatchEvent(new CustomEvent('chatia:navigate-tab', { detail: { tab: ev.navigate_tab } })); },
          onProposalCreated: (newId) => { sessionStorage.setItem('chatia:onboarding-active', String(newId)); navigate(`/proposals/${newId}`); },
          onDone: (finalText, extra) => {
            setMessages(prev => [...prev, { role: 'assistant', content: finalText || acc }]);
            setStreamingText('');
            setPendingCall(extra?.pending_call ?? null);
          },
          onError: (err) => { setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${err}` }]); setStreamingText(''); },
        },
      );
    } catch (e: any) {
      setMessages(prev => [...prev, { role: 'assistant', content: `⚠️ ${e?.message || 'Erro ao confirmar.'}` }]);
    } finally {
      setConfirming(false);
    }
  };

  const handleExitOnboarding = async () => {
    // Não é destrutivo: a proposta e o que já foi feito ficam; a conversa volta ao modo
    // normal. Um pedido explícito ("monta a proposta") reativa o fluxo guiado depois.
    await chatOnboardingService.abort(proposalId ?? null);
    setOnboarding(false);
    setOnbState(null);
    setPendingCall(null);
    sessionStorage.removeItem('chatia:onboarding-active');
    setMessages(prev => [...prev, { role: 'assistant', content: 'Saímos do passo a passo. Pode me perguntar qualquer coisa sobre a proposta. Se quiser retomar a construção guiada, é só pedir.' }]);
  };

  const handleClearHistory = async () => {
    if (!window.confirm('Tem certeza que deseja limpar o histórico desta conversa?')) return;
    // Invalida qualquer resposta de IA em voo — se uma chegar depois daqui, é
    // descartada em vez de reaparecer na conversa recém-limpa (ver sendMessageText).
    conversationGenerationRef.current += 1;
    // Zera a conversa localmente já — feedback imediato pro usuário.
    setMessages([INITIAL_MESSAGE]);
    if (proposalId) {
      chatMemoryService.clearHistory(proposalId);
      setOnboarding(false);
      setOnbState(null);
      setPendingCall(null);
      sessionStorage.removeItem('chatia:onboarding-active');
      try {
        await chatService.deleteHistory(proposalId);
      } catch {
        // Falha ao limpar no backend: mantém limpo localmente nesta sessão. Não
        // mexe em historyLoaded aqui — se mexesse, o efeito de "carregar histórico
        // ao abrir" dispararia de novo com historyLoaded=false e isOpen=true,
        // buscando o histórico do backend numa corrida contra o delete acima e
        // podendo repopular a conversa recém-limpa com o histórico antigo.
      }
    }
  };

  const handleOpenHistoryBrowser = () => {
    setHistoryList(chatMemoryService.listHistories());
    setShowHistoryBrowser(true);
  };

  const handleLoadFromHistory = (summary: ChatHistorySummary) => {
    setShowHistoryBrowser(false);
    navigate(`/proposals/${summary.proposalId}`);
  };

  const handleFeedback = async (msgIndex: number, rating: 1 | -1) => {
    if (feedbackGiven.has(msgIndex)) return;
    const assistantMsg = messages[msgIndex];
    const userMsg = messages[msgIndex - 1];
    if (!assistantMsg || assistantMsg.role !== 'assistant') return;
    setFeedbackGiven(prev => new Map(prev).set(msgIndex, rating));
    await chatService.sendFeedback({
      proposalId,
      message: userMsg?.content || '',
      response: assistantMsg.content,
      rating,
    });
  };

  const formatRelativeTime = (timestamp: number): string => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (days > 0) return `há ${days} dia${days > 1 ? 's' : ''}`;
    if (hours > 0) return `há ${hours}h`;
    if (minutes > 0) return `há ${minutes}min`;
    return 'agora';
  };

  const quickActions = getDynamicQuickActions(
    roadmapItemsRef.current,
    checklistItemsRef.current.length,
    docChecklistItemsRef.current.length
  );

  const fileSearchActive = lastMetadata?.file_search_active;

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <div style={{ position: 'fixed', bottom: '84px', right: '20px', zIndex: 50 }}>
          <button
            onClick={() => setIsOpen(true)}
            onMouseEnter={() => setChatBtnHovered(true)}
            onMouseLeave={() => setChatBtnHovered(false)}
            style={{
              display: 'flex', alignItems: 'center', backgroundColor: '#10B981',
              borderRadius: '999px', border: 'none', cursor: 'pointer',
              height: '50px', width: chatBtnHovered ? '200px' : '50px',
              overflow: 'hidden', transition: 'width 0.3s ease, box-shadow 0.2s ease',
              boxShadow: chatBtnHovered ? '0 6px 20px rgba(16,185,129,0.45)' : '0 4px 16px rgba(16,185,129,0.35)',
              padding: 0,
            }}
            title="Especialista"
          >
            <span style={{ color: '#fff', fontSize: '0.875rem', fontWeight: 600, whiteSpace: 'nowrap', paddingLeft: chatBtnHovered ? '16px' : '0', opacity: chatBtnHovered ? 1 : 0, transition: 'opacity 0.2s ease 0.1s', flex: 1, overflow: 'hidden', minWidth: 0 }}>
              Especialista
            </span>
            <div style={{ width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Sparkles size={22} color="#fff" />
            </div>
          </button>
        </div>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          style={isExpanded ? { position: 'fixed', inset: 0, zIndex: 60, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' } : {}}
          onClick={isExpanded ? () => setIsExpanded(false) : undefined}
        >
          <div
            ref={isExpanded ? undefined : panelRef}
            style={isExpanded ? {
              width: '100%', maxWidth: '860px', height: '85vh',
              backgroundColor: '#ffffff', borderRadius: '16px', boxShadow: '0 24px 64px rgba(0,0,0,0.3)',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            } : {
              position: 'fixed', bottom: '86px', right: '20px', zIndex: 50,
              width: `${chatWidth}px`, height: `${chatHeight}px`,
              transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
              backgroundColor: '#ffffff', borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e5e7eb',
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}
            onClick={e => e.stopPropagation()}
          >
            {/* Resize handles */}
            {!isExpanded && <>
              <div onMouseDown={handleTopResize} style={{ position: 'absolute', top: 0, left: '16px', right: '16px', height: '6px', cursor: 'ns-resize', zIndex: 10 }} />
              <div onMouseDown={handleLeftResize} style={{ position: 'absolute', top: '16px', left: 0, bottom: '16px', width: '6px', cursor: 'ew-resize', zIndex: 10 }} />
              <div onMouseDown={handleCornerResize} style={{ position: 'absolute', top: 0, left: 0, width: '16px', height: '16px', cursor: 'nwse-resize', zIndex: 11, borderTopLeftRadius: '16px' }} />
            </>}

            {/* Header — arrastável (clique fora dos botões) para mover o painel pela tela */}
            <div
              onMouseDown={isExpanded ? undefined : handleDragStart}
              style={{ padding: '12px 16px 10px', background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)', color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', flexShrink: 0, cursor: isExpanded ? 'default' : 'grab' }}
            >
              <p style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0, letterSpacing: '-0.01em', textAlign: 'center' }}>
                Especialista
              </p>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px', width: '100%' }}>
                {/* Toggle contexto */}
                <button
                  onClick={() => proposalId && setUseProposalContext(v => !v)}
                  title={!proposalId ? 'Abra uma proposta para ativar o contexto' : useProposalContext ? 'A IA está usando o conteúdo da proposta. Clique para desativar.' : 'A IA não está usando a proposta. Clique para ativar.'}
                  style={{ border: 'none', cursor: proposalId ? 'pointer' : 'not-allowed', padding: '5px 8px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'background 0.15s', backgroundColor: proposalId && useProposalContext ? 'rgba(52,211,153,0.22)' : 'transparent', color: !proposalId ? 'rgba(255,255,255,0.3)' : useProposalContext ? '#6ee7b7' : 'rgba(255,255,255,0.5)', opacity: proposalId ? 1 : 0.55 }}
                  onMouseEnter={e => { if (!proposalId) return; (e.currentTarget as HTMLElement).style.backgroundColor = useProposalContext ? 'rgba(52,211,153,0.32)' : 'rgba(255,255,255,0.14)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = proposalId && useProposalContext ? 'rgba(52,211,153,0.22)' : 'transparent'; }}
                >
                  {proposalId && useProposalContext ? <DocumentTextIcon style={{ width: '16px', height: '16px' }} /> : <DocumentMinusIcon style={{ width: '16px', height: '16px' }} />}
                  <span style={{ fontSize: '9px', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>{!proposalId ? 'Sem proposta' : useProposalContext ? 'Com proposta' : 'Sem proposta'}</span>
                </button>

                {/* Histórico */}
                <button onClick={handleOpenHistoryBrowser} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.75)', padding: '5px 7px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'background 0.15s, color 0.15s' }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.16)'; (e.currentTarget as HTMLElement).style.color = '#fff'; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.75)'; }}>
                  <ClockIcon style={{ width: '16px', height: '16px' }} />
                  <span style={{ fontSize: '9px', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>Histórico</span>
                </button>

                {/* Limpar */}
                {proposalId && (
                  <button onClick={handleClearHistory} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.75)', padding: '5px 7px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'background 0.15s, color 0.15s' }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(239,68,68,0.28)'; (e.currentTarget as HTMLElement).style.color = '#fca5a5'; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.75)'; }}>
                    <TrashIcon style={{ width: '16px', height: '16px' }} />
                    <span style={{ fontSize: '9px', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>Limpar</span>
                  </button>
                )}

                {/* Expandir */}
                <button onClick={() => setIsExpanded(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.75)', padding: '5px 7px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'background 0.15s, color 0.15s' }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.16)'; (e.currentTarget as HTMLElement).style.color = '#fff'; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.75)'; }}>
                  {isExpanded ? <ArrowsPointingInIcon style={{ width: '16px', height: '16px' }} /> : <ArrowsPointingOutIcon style={{ width: '16px', height: '16px' }} />}
                  <span style={{ fontSize: '9px', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>{isExpanded ? 'Reduzir' : 'Expandir'}</span>
                </button>

                {/* Fechar */}
                <button onClick={() => { setIsOpen(false); setIsExpanded(false); setChatBtnHovered(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.75)', padding: '5px 7px', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', transition: 'background 0.15s, color 0.15s' }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.16)'; (e.currentTarget as HTMLElement).style.color = '#fff'; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.75)'; }}>
                  <XMarkIcon style={{ width: '16px', height: '16px' }} />
                  <span style={{ fontSize: '9px', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>Fechar</span>
                </button>
              </div>

              {/* Status da proposta ativa */}
              {proposalId && proposalTitle && useProposalContext ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: 'rgba(0,0,0,0.18)', borderRadius: '20px', padding: '3px 10px', maxWidth: '100%' }}>
                  <DocumentTextIcon style={{ width: '11px', height: '11px', flexShrink: 0, color: '#6ee7b7' }} />
                  <span style={{ fontSize: '10px', fontWeight: 600, color: 'rgba(255,255,255,0.9)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {proposalTitle.length > 42 ? proposalTitle.slice(0, 42) + '…' : proposalTitle}
                  </span>
                </div>
              ) : proposalId && !proposalContext ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '20px', padding: '3px 10px' }}>
                  <Loader2 style={{ width: '10px', height: '10px', flexShrink: 0, color: 'rgba(255,255,255,0.6)', animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>Carregando contexto…</span>
                </div>
              ) : null}
            </div>

            {/* Badge: file_search ativo */}
            {fileSearchActive && (
              <div style={{ padding: '5px 16px', backgroundColor: '#ecfdf5', borderBottom: '1px solid #6ee7b7', fontSize: '0.7rem', color: '#065f46', display: 'flex', alignItems: 'center', gap: '5px' }}>
                <FileSearch style={{ width: '12px', height: '12px' }} />
                Consultando documentos anexados
              </div>
            )}

            {/* Badge: fallback de provider */}
            {lastMetadata?.fallback_reason && (
              <div style={{ padding: '6px 16px', backgroundColor: '#FEF3C7', borderBottom: '1px solid #F59E0B', fontSize: '0.7rem', color: '#92400E', display: 'flex', alignItems: 'center', gap: '4px' }}>
                ⚠️ Respondido por <strong>{lastMetadata.model_used}</strong> ({lastMetadata.fallback_reason})
              </div>
            )}

            {/* Notificação de histórico */}
            {showHistoryNotification && (
              <div style={{ padding: '8px 16px', backgroundColor: '#D1FAE5', borderBottom: '1px solid #10B981', fontSize: '0.75rem', color: '#065F46', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ClockIcon style={{ width: '14px', height: '14px' }} />
                Histórico anterior carregado com {messages.length - 1} mensagens
              </div>
            )}

            {/* Painel de histórico */}
            {showHistoryBrowser ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <button onClick={() => setShowHistoryBrowser(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', padding: '2px' }}>
                    <ChevronLeft style={{ width: '18px', height: '18px' }} />
                  </button>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#1f2937' }}>Histórico de Conversas</span>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
                  {historyList.length === 0 ? (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>Nenhum histórico salvo ainda.</div>
                  ) : historyList.map(item => (
                    <button key={item.proposalId} onClick={() => handleLoadFromHistory(item)}
                      style={{ width: '100%', padding: '10px 16px', background: proposalId === item.proposalId ? '#F0FDF4' : 'none', border: 'none', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1f2937', lineHeight: 1.3 }}>{item.proposalTitle}</span>
                      <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{item.messageCount} mensagem{item.messageCount !== 1 ? 's' : ''} · {formatRelativeTime(item.lastAccessed)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {messages.map((msg, idx) => (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                      <div style={{ maxWidth: '85%', padding: '10px 14px', borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px', backgroundColor: msg.role === 'user' ? '#10B981' : '#f3f4f6', color: msg.role === 'user' ? '#fff' : '#1f2937', fontSize: '0.875rem', lineHeight: '1.5', wordBreak: 'break-word', ...(msg.role === 'user' ? { whiteSpace: 'pre-wrap' as const } : {}) }}>
                        {msg.role === 'assistant' ? <MarkdownRenderer content={msg.content} /> : msg.content}
                      </div>
                      {/* Thumbs up/down nas respostas da IA */}
                      {msg.role === 'assistant' && idx > 0 && (
                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px', opacity: feedbackGiven.has(idx) ? 0.5 : 1 }}>
                          <button onClick={() => handleFeedback(idx, 1)} title="Resposta útil" style={{ background: 'none', border: 'none', cursor: feedbackGiven.has(idx) ? 'default' : 'pointer', padding: '2px', color: feedbackGiven.get(idx) === 1 ? '#10B981' : '#d1d5db' }}>
                            <ThumbsUp style={{ width: '12px', height: '12px' }} />
                          </button>
                          <button onClick={() => handleFeedback(idx, -1)} title="Resposta ruim" style={{ background: 'none', border: 'none', cursor: feedbackGiven.has(idx) ? 'default' : 'pointer', padding: '2px', color: feedbackGiven.get(idx) === -1 ? '#ef4444' : '#d1d5db' }}>
                            <ThumbsDown style={{ width: '12px', height: '12px' }} />
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Onboarding: barra de progresso das 4 etapas */}
                  {onboarding && onbState && !['concluido', 'abortado', 'sem_proposta'].includes(onbState.state) && (() => {
                    const STEPS = ['Mapa Conceitual', 'Roteiro', 'Elaboração', 'Conclusão'];
                    const HINT: Record<number, string> = {
                      1: 'definir a ideia e gerar o mapa conceitual',
                      2: 'montar as seções da proposta',
                      3: 'a IA escreve o conteúdo de todas as seções',
                      4: 'revisar o resultado',
                    };
                    return (
                      <div style={{ margin: '6px 0', padding: '10px 12px', borderRadius: '10px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe' }}>
                        <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                          {STEPS.map((_, i) => (
                            <div key={i} style={{ flex: 1, height: '4px', borderRadius: '2px', backgroundColor: i < onbState.step ? '#3b82f6' : '#dbeafe' }} />
                          ))}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', fontSize: '0.75rem', color: '#1e40af' }}>
                          <span>
                            <strong>Etapa {onbState.step} de {onbState.total_steps} — {STEPS[onbState.step - 1] || ''}</strong>
                            <span style={{ opacity: 0.75 }}>: {HINT[onbState.step] || ''}</span>
                          </span>
                          <button
                            type="button"
                            onClick={handleExitOnboarding}
                            style={{ flexShrink: 0, background: 'none', border: 'none', color: '#3b82f6', fontSize: '0.72rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
                          >
                            Sair do passo a passo
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Onboarding: card de confirmação de uma ação que custa tokens */}
                  {pendingCall && !confirming && (() => {
                    const LABEL: Record<string, string> = {
                      gerar_mapa_conceitual: 'gerar o rascunho do Mapa Conceitual',
                      gerar_roteiro: 'montar as seções do Roteiro',
                      elaborar_proposta_completa: 'escrever o conteúdo de todas as seções',
                      elaborar_secao: 'escrever o conteúdo desta seção',
                    };
                    const what = LABEL[pendingCall.name] || pendingCall.name.replace(/_/g, ' ');
                    return (
                      <div style={{ margin: '6px 0', padding: '12px 14px', borderRadius: '12px', backgroundColor: '#fffbeb', border: '1px solid #fcd34d' }}>
                        <p style={{ margin: '0 0 8px', fontSize: '0.8rem', color: '#92400e' }}>
                          Posso <strong>{what}</strong>? Essa etapa usa a IA e pode levar alguns instantes.
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={() => handleConfirmCall('approve')}
                            style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', backgroundColor: '#10B981', color: '#fff', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                            Pode gerar
                          </button>
                          <button onClick={() => handleConfirmCall('reject')}
                            style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#6b7280', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                            Ainda não
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  {confirming && (
                    <div style={{ margin: '6px 0', padding: '10px 14px', borderRadius: '12px', backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', fontSize: '0.78rem', color: '#065f46' }}>
                      Gerando… isso pode levar alguns instantes.
                    </div>
                  )}

                  {/* Streaming em tempo real */}
                  {streamingText && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <div style={{ maxWidth: '85%', padding: '10px 14px', borderRadius: '16px 16px 16px 4px', backgroundColor: '#f3f4f6', color: '#1f2937', fontSize: '0.875rem', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {streamingText}
                        <span style={{ display: 'inline-block', width: '8px', height: '14px', backgroundColor: '#10B981', marginLeft: '2px', animation: 'blink 1s step-end infinite', verticalAlign: 'text-bottom' }} />
                      </div>
                    </div>
                  )}

                  {/* Boas-vindas */}
                  {messages.length === 1 && !loading && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '4px 0 8px' }}>
                      <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '14px 16px', fontSize: '0.8rem', color: '#065F46', lineHeight: '1.6' }}>
                        <p style={{ margin: '0 0 6px', fontWeight: 700, fontSize: '0.85rem' }}>O que posso fazer por você:</p>
                        <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                          <li>Analisar e melhorar seções da proposta</li>
                          <li>Verificar adequação ao edital</li>
                          <li>Sugerir e aplicar alterações diretamente no roteiro</li>
                          <li>Responder dúvidas sobre o conteúdo</li>
                        </ul>
                      </div>
                      <p style={{ margin: '4px 0 2px', fontSize: '0.72rem', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sugestões rápidas</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {quickActions.map(action => (
                          <button key={action.label} onClick={() => sendMessageText(action.prompt)}
                            style={{ padding: '7px 14px', borderRadius: '20px', border: '1.5px solid #10B981', backgroundColor: '#fff', color: '#059669', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.backgroundColor = '#10B981'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.backgroundColor = '#fff'; (e.currentTarget as HTMLElement).style.color = '#059669'; }}>
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {loading && !streamingText && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{ padding: '10px 14px', borderRadius: '16px 16px 16px 4px', backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Loader2 style={{ width: '14px', height: '14px' }} className="animate-spin" />
                        Pensando...
                      </div>
                    </div>
                  )}

                  {/* Card de edição inline com diff */}
                  {pendingEdit && !applyingEdit && (() => {
                    const currentContent = getCurrentContent(pendingEdit);
                    return (
                      <div style={{ border: '1px solid #10B981', borderRadius: '12px', padding: '12px 14px', backgroundColor: '#f0fdf4', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#065F46', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Sparkles style={{ width: '14px', height: '14px' }} />
                          {pendingEdit.edit_type === 'elaboration_content' && `Sugestão para seção — ${pendingEdit.topic}`}
                          {pendingEdit.edit_type === 'roadmap_instruction' && `Instrução para seção — ${pendingEdit.topic}`}
                          {pendingEdit.edit_type === 'concept_map_field' && `Campo das Orientações — ${pendingEdit.field_label || pendingEdit.field}`}
                          {pendingEdit.edit_type === 'checklist_item_add' && `Novo critério — Checklist ${pendingEdit.checklist_type === 'documental' ? 'Documental' : 'Enquadramento'}`}
                          {pendingEdit.edit_type === 'checklist_evaluation' && `Avaliação — ${pendingEdit.item_text || 'item do checklist'}`}
                        </div>

                        {/* Diff: Antes / Depois */}
                        {currentContent && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div>
                              <p style={{ margin: '0 0 3px', fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>Antes</p>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px', padding: '7px 10px', maxHeight: '100px', overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                                {currentContent.length > 400 ? currentContent.slice(0, 400) + '…' : currentContent}
                              </div>
                            </div>
                            <div>
                              <p style={{ margin: '0 0 3px', fontSize: '0.7rem', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>Depois</p>
                              <div style={{ fontSize: '0.75rem', color: '#374151', backgroundColor: '#fff', border: '1px solid #d1fae5', borderRadius: '6px', padding: '7px 10px', maxHeight: '120px', overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: '1.4' }}>
                                {pendingEdit.proposed_content}
                              </div>
                            </div>
                          </div>
                        )}
                        {!currentContent && (
                          <div style={{ fontSize: '0.78rem', color: '#374151', backgroundColor: '#fff', border: '1px solid #d1fae5', borderRadius: '8px', padding: '8px 10px', maxHeight: '200px', overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                            {pendingEdit.proposed_content}
                          </div>
                        )}

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button onClick={applyEdit} style={{ flex: 1, padding: '7px 12px', borderRadius: '8px', border: 'none', backgroundColor: '#10B981', color: '#fff', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                            <Check style={{ width: '13px', height: '13px' }} />
                            {pendingEdit.edit_type === 'elaboration_content' && 'Aplicar na Elaboração'}
                            {pendingEdit.edit_type === 'roadmap_instruction' && 'Aplicar no Roteiro'}
                            {pendingEdit.edit_type === 'concept_map_field' && 'Aplicar nas Orientações'}
                            {pendingEdit.edit_type === 'checklist_item_add' && 'Adicionar à Revisão'}
                            {pendingEdit.edit_type === 'checklist_evaluation' && 'Aplicar Avaliação'}
                          </button>
                          <button onClick={rejectEdit} style={{ padding: '7px 12px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#6b7280', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                            <Ban style={{ width: '13px', height: '13px' }} />
                            Descartar
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {applyingEdit && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                      <div style={{ padding: '10px 14px', borderRadius: '16px 16px 16px 4px', backgroundColor: '#f0fdf4', color: '#065F46', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #d1fae5' }}>
                        <Loader2 style={{ width: '14px', height: '14px' }} className="animate-spin" />
                        Aplicando alteração...
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '8px', flexShrink: 0, backgroundColor: '#fafafa' }}>
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={e => { setInput(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 180) + 'px'; }}
                    onKeyDown={handleKeyDown}
                    placeholder="Pergunte algo ou peça uma sugestão..."
                    disabled={loading}
                    rows={2}
                    style={{ flex: 1, padding: '10px 14px', borderRadius: '16px', border: '1px solid #d1d5db', outline: 'none', fontSize: '0.875rem', backgroundColor: loading ? '#f9fafb' : '#fff', resize: 'vertical', overflow: 'auto', lineHeight: '1.5', minHeight: '60px', maxHeight: '180px' }}
                  />
                  <button
                    onClick={handleSend}
                    disabled={loading || !input.trim()}
                    style={{ width: '40px', height: '40px', borderRadius: '50%', border: 'none', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer', backgroundColor: loading || !input.trim() ? '#d1d5db' : '#10B981', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                  >
                    <PaperAirplaneIcon style={{ width: '16px', height: '16px' }} />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
